import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COMMUNITY_SECURE_GLOBAL_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { commentsKeys } from 'modules/comments/api/key-factory'
import type { ReportRequest } from '../types/moderation'
import { communityKeys, communityReportKeys } from './key-factory'

export const useReportComment = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void
  onError?: (error: Error) => void
}): UseMutationResult<
  void,
  Error,
  {
    body: ReportRequest
    communityPath: string
    postId: number
    commentId: number
  }
> => {
  const queryClient = useQueryClient()

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    void,
    ReportRequest
  >({
    method: RequestMethodsEnum.post,
    badRequestHandler: () => {},
    notFoundResponseHandler: () => {},
    accessDeniedResponseHandler: () => {},
    unprocessableContentHandler: () => {},
    conflictResponseHandler: () => {},
  })

  return useMutation({
    mutationFn: ({ body, communityPath, postId, commentId }) =>
      fetcher(
        `${COMMUNITY_SECURE_GLOBAL_PREFIX}/${communityPath}/report/comment/${commentId}?postId=${postId}`,
        body,
      ),

    onSuccess: onSuccess ? () => onSuccess() : undefined,

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: communityKeys.commentGeneral() })
      void queryClient.invalidateQueries({ queryKey: communityReportKeys.all })

      void queryClient.invalidateQueries({ queryKey: commentsKeys.all })
    },

    onError: onError ? error => onError(error) : undefined,
  })
}
